import { useFormContext, useWatch } from "react-hook-form";
import { useDebounce } from "react-use";
import useAddAnswerToFormInstance from "../useAddAnswerToFormInstance/useAddAnswerToFormInstance";
import useEditAnswer from "../useEditAnswer/useEditAnswer";
import useFormInstance from "../useFormInstance/useFormInstance";
import { useEffect } from "react";

interface Props extends Pick<
	ObservationQuestion,
	"questionType" | "questionNumber" | "id"
> {
	observationType?: IObservationType;
}

const useSubmitAnswer = ({
	questionType,
	questionNumber,
	id: questionId,
	observationType
}: Props) => {
	const DEBOUNCE_DURATION = questionType === "TEXT" ? 2000 : 1000;

	const {
		mutate: editAnswer,
		isLoading: editAnswerLoading,
		isError: editError
	} = useEditAnswer();

	const {
		mutate: addAnswer,
		isLoading: submitAnswerLoading,
		isError: addError
	} = useAddAnswerToFormInstance();

	const { control, setValue, getValues } =
		useFormContext<ObservationFormState>();

	const { setError, clearErrors } = useFormContext();

	const submittedAnswers = useWatch({
		name: "submittedAnswers",
		control
	});

	const type = useWatch({
		name: `questions.${questionNumber - 1}.questionType`,
		control
	});

	const answer = useWatch({
		name: `questions.${questionNumber - 1}.givenAnswer`,
		control
	});

	const action = useWatch({
		name: `questions.${questionNumber - 1}.action`,
		control
	});

	const images = useWatch({
		name: `questions.${questionNumber - 1}.images`,
		control
	});

	const formInstanceId = useWatch({
		name: "formInstanceId",
		control
	});

	const onSubmitSuccess = (response: IObservationForm) => {
		clearErrors(`questions.${questionNumber - 1}.givenAnswer`);

		setValue("submittedAnswers", [
			...submittedAnswers,
			{
				questionNumber,
				value: answer,
				responseId: response.id
			}
		]);

		// We need to get a fresh copy to ensure we don't overlap with instances
		const submittingQuestions = getValues("isSubmittingAnswer");
		setValue(
			"isSubmittingAnswer",
			submittingQuestions.filter(
				(submittedAnswerQuestionNum) =>
					submittedAnswerQuestionNum !== questionNumber
			)
		);

		refetchOverallScore();
	};

	const onEditSuccess = () => {
		clearErrors(`questions.${questionNumber - 1}.givenAnswer`);

		// We need to get a fresh copy to ensure we don't overlap with instances
		const submittingQuestions = getValues("isSubmittingAnswer");
		setValue(
			"isSubmittingAnswer",
			submittingQuestions.filter(
				(submittedAnswerQuestionNum) =>
					submittedAnswerQuestionNum !== questionNumber
			)
		);

		refetchOverallScore();
	};

	const questionWithChoices =
		questionType === "RADIO" || questionType === "SELECT";

	const refetchOverallScore = async () => {
		if (!questionWithChoices && observationType !== "GREAT_CONVERSATION")
			return;

		const { data: formInstance } = await refetch();

		if (formInstance) {
			setValue(
				"overallScore",
				formInstance.overall_score ? formInstance.overall_score : 0
			);
		}
	};

	const {
		refetch,
		isFetching: isInstanceLoading, // isLoading is true when enabled = false, have to use fetching instead here
		isRefetching: isInstanceRefetching
	} = useFormInstance(formInstanceId, { enabled: false });

	const submitAnswer = async () => {
		if (
			(!answer && !action && (!images || images.length === 0)) ||
			!formInstanceId
		)
			return;

		// We need to get a fresh copy to ensure we don't overlap with instances
		const submittingQuestions = getValues("isSubmittingAnswer");

		// Our question might already be loading, so only add it into the state if we haven't already
		if (!submittingQuestions.includes(questionNumber)) {
			setValue("isSubmittingAnswer", [...submittingQuestions, questionNumber]);
		}

		const submittedAnswer = submittedAnswers.find(
			(submittedAnswer) => submittedAnswer.questionNumber === questionNumber
		);

		if (submittedAnswer) {
			editAnswer(
				{
					id: submittedAnswer?.responseId,
					value: answer,
					action: action,
					images,
					observationinstance: formInstanceId
				},
				{ onSuccess: onEditSuccess }
			);
		} else {
			const { data } = await refetch();

			const isQuestionAnswered = data?.observationinstance_response.find(
				(question) => question.observationquestion.id === questionId
			);

			if (isQuestionAnswered) {
				editAnswer(
					{
						id: isQuestionAnswered.id,
						value: answer,
						action: action,
						images,
						observationinstance: formInstanceId
					},
					{ onSuccess: onEditSuccess }
				);
			} else {
				addAnswer(
					{
						observationquestion: questionId,
						observationinstance: formInstanceId,
						value: answer,
						images,
						action: action
					},
					{ onSuccess: onSubmitSuccess }
				);
			}
		}
	};

	useDebounce(() => submitAnswer(), DEBOUNCE_DURATION, [
		answer,
		action,
		images
	]);

	const submitHeading = async () => {
		if (!formInstanceId) return;

		if (type === "HEADING") {
			const { data } = await refetch();

			const questionExists = data?.observationinstance_response.find(
				(observation) => observation.observationquestion.id === questionId
			);

			if (questionExists) return;

			addAnswer(
				{
					observationquestion: questionId,
					observationinstance: formInstanceId,
					value: ""
				},
				{ onSuccess: onSubmitSuccess }
			);
		}
	};

	useEffect(() => {
		submitHeading();
	}, [type, formInstanceId]);

	useEffect(() => {
		if (type !== "HEADING" && (addError || editError)) {
			setError(`questions.${questionNumber - 1}.givenAnswer`, {
				type: "networkIssue",
				message:
					"Failed to save answer, please check or reselect your answers and try again."
			});
		}
	}, [addError, editError]);

	return {
		loading:
			editAnswerLoading ||
			submitAnswerLoading ||
			isInstanceLoading ||
			isInstanceRefetching,
		retrySave: submitAnswer
	};
};

export default useSubmitAnswer;
